import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';

function Alerts({ showToast }) {
  const { warnings, updateWarning, deleteWarning, addWarningHistory } = useData();
  
  const [activeTab, setActiveTab] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  const getRiskLabel = (riskLevel) => {
    const map = {
      'high': 'Cao',
      'medium': 'Trung bình',
      'low': 'Thấp',
      'none': 'An toàn'
    };
    return map[riskLevel] || riskLevel;
  };

  const getStatusLabel = (status) => {
    const map = {
      'new': 'Mới',
      'in_progress': 'Đang xử lý',
      'resolved': 'Đã xử lý'
    };
    return map[status] || status;
  };

  const filterAlerts = () => {
    return warnings.filter(warning => {
      const riskLabel = getRiskLabel(warning.riskLevel);
      const statusLabel = getStatusLabel(warning.status);
      
      const matchesTab = activeTab === 'all' || 
        (activeTab === 'high' && warning.riskLevel === 'high') ||
        (activeTab === 'medium' && warning.riskLevel === 'medium') ||
        (activeTab === 'low' && warning.riskLevel === 'low');
      
      const matchesSearch = warning.customerName.toLowerCase().includes(searchText.toLowerCase()) ||
        warning.customerId.toLowerCase().includes(searchText.toLowerCase()) ||
        warning.code.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesStatus = !statusFilter || statusLabel === statusFilter;
      const matchesType = !typeFilter || warning.alertType === typeFilter;
      
      return matchesTab && matchesSearch && matchesStatus && matchesType;
    });
  };

  const getRiskBadgeClass = (riskLevel) => {
    if (riskLevel === 'high') return 'danger';
    if (riskLevel === 'medium') return 'warning';
    if (riskLevel === 'low') return 'info';
    return 'success';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'new') return 'primary';
    if (status === 'in_progress') return 'warning';
    return 'success';
  };

  const viewAlert = (alert) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };

  const updateAlertStatus = (warningId, newStatus) => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const warning = warnings.find(w => w.id === warningId);
    
    const statusMap = {
      'Mới': 'new',
      'Đang xử lý': 'in_progress',
      'Đã xử lý': 'resolved'
    };
    const actualStatus = statusMap[newStatus] || newStatus;
    
    updateWarning(warningId, {
      status: actualStatus,
      lastCheckedDate: now,
      lastCheckedBy: 'Ngô Văn A'
    });

    addWarningHistory(warningId, {
      date: now,
      score: warning.score,
      riskLevel: warning.riskLevel,
      status: actualStatus,
      action: 'Cập nhật trạng thái',
      user: 'Ngô Văn A',
      notes: `Chuyển trạng thái sang ${newStatus}`
    });

    showToast(`Đã cập nhật trạng thái cảnh báo ${warningId}`, 'success');
  };

  const deleteAlert = (warningId) => {
    if (window.confirm('Bạn có chắc muốn xóa cảnh báo này?')) {
      deleteWarning(warningId);
      showToast(`Đã xóa cảnh báo ${warningId}`, 'success');
    }
  };

  const processAlert = (warning) => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    updateWarning(warning.id, {
      status: 'in_progress',
      assignedTo: 'Ngô Văn A',
      lastCheckedDate: now,
      lastCheckedBy: 'Ngô Văn A'
    });

    addWarningHistory(warning.id, {
      date: now,
      score: warning.score,
      riskLevel: warning.riskLevel,
      status: 'in_progress',
      action: 'Bắt đầu xử lý',
      user: 'Ngô Văn A',
      notes: 'Đã gửi cảnh báo vào quy trình xử lý'
    });

    showToast('Đã gửi cảnh báo vào quy trình xử lý', 'success');
    setTimeout(() => {
      setShowDetailModal(false);
    }, 1500);
  };

  const exportAlerts = () => {
    showToast('Đang xuất file Excel...', 'info');
    setTimeout(() => {
      showToast('Đã xuất file Excel thành công', 'success');
    }, 1000);
  };

  const filteredAlerts = filterAlerts();

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-exclamation-triangle me-2"></i>
          Quản lý Cảnh báo sớm
        </h2>
        <button className="btn btn-success" onClick={exportAlerts}>
          <i className="bi bi-file-excel me-1"></i>
          Xuất Excel
        </button>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Tất cả <span className="badge bg-secondary ms-1">{warnings.length}</span>
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'high' ? 'active' : ''}`}
            onClick={() => setActiveTab('high')}
          >
            Rủi ro cao <span className="badge bg-danger ms-1">
              {warnings.filter(w => w.riskLevel === 'high').length}
            </span>
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'medium' ? 'active' : ''}`}
            onClick={() => setActiveTab('medium')}
          >
            Rủi ro trung bình <span className="badge bg-warning ms-1">
              {warnings.filter(w => w.riskLevel === 'medium').length}
            </span>
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'low' ? 'active' : ''}`}
            onClick={() => setActiveTab('low')}
          >
            Rủi ro thấp <span className="badge bg-info ms-1">
              {warnings.filter(w => w.riskLevel === 'low').length}
            </span>
          </button>
        </li>
      </ul>

      {/* Search and Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input 
                type="text" 
                className="form-control" 
                placeholder="Tìm theo mã KH, tên khách hàng..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select 
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tất cả trạng thái</option>
                <option value="Mới">Mới</option>
                <option value="Đang xử lý">Đang xử lý</option>
                <option value="Đã xử lý">Đã xử lý</option>
              </select>
            </div>
            <div className="col-md-3">
              <select 
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Tất cả loại cảnh báo</option>
                <option value="Dư nợ quá hạn">Dư nợ quá hạn</option>
                <option value="Thu nhập giảm đột ngột">Thu nhập giảm</option>
                <option value="Thanh toán trễ">Thanh toán trễ</option>
                <option value="Nợ quá hạn 60 ngày">Nợ quá hạn 60 ngày</option>
                <option value="Biến động thu nhập">Biến động thu nhập</option>
                <option value="Giao dịch bất thường">Giao dịch bất thường</option>
              </select>
            </div>
            <div className="col-md-2">
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchText('');
                  setStatusFilter('');
                  setTypeFilter('');
                  showToast('Đã xóa bộ lọc', 'info');
                }}
              >
                <i className="bi bi-x-circle"></i> Xóa lọc
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Mã KH</th>
                  <th>Khách hàng</th>
                  <th>Loại cảnh báo</th>
                  <th>Mức độ</th>
                  <th>Điểm</th>
                  <th>Trạng thái</th>
                  <th>Thời gian</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((warning, index) => (
                  <tr key={warning.id}>
                    <td><strong>{warning.customerId}</strong></td>
                    <td>{warning.customerName}</td>
                    <td>{warning.alertType}</td>
                    <td>
                      <span className={`badge bg-${getRiskBadgeClass(warning.riskLevel)}`}>
                        {getRiskLabel(warning.riskLevel)}
                      </span>
                    </td>
                    <td><strong>{warning.score}/100</strong></td>
                    <td>
                      <span className={`badge bg-${getStatusBadgeClass(warning.status)}`}>
                        {getStatusLabel(warning.status)}
                      </span>
                    </td>
                    <td><small className="text-muted">{warning.createdDate}</small></td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button 
                          className="btn btn-outline-primary"
                          onClick={() => viewAlert(warning)}
                          title="Xem chi tiết"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        {warning.status !== 'in_progress' && (
                          <button 
                            className="btn btn-outline-success"
                            onClick={() => updateAlertStatus(warning.id, 'Đang xử lý')}
                            title="Bắt đầu xử lý"
                          >
                            <i className="bi bi-play-fill"></i>
                          </button>
                        )}
                        {warning.status === 'in_progress' && (
                          <button 
                            className="btn btn-outline-info"
                            onClick={() => updateAlertStatus(warning.id, 'Đã xử lý')}
                            title="Hoàn thành"
                          >
                            <i className="bi bi-check-circle"></i>
                          </button>
                        )}
                        <button 
                          className="btn btn-outline-danger"
                          onClick={() => deleteAlert(warning.id)}
                          title="Xóa"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAlert && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Chi tiết cảnh báo - {selectedAlert.code}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDetailModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6 className="text-primary">Thông tin khách hàng</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th>Mã Cảnh báo:</th>
                          <td><strong>{selectedAlert.code}</strong></td>
                        </tr>
                        <tr>
                          <th>Mã KH:</th>
                          <td>{selectedAlert.customerId}</td>
                        </tr>
                        <tr>
                          <th>Tên:</th>
                          <td>{selectedAlert.customerName}</td>
                        </tr>
                        <tr>
                          <th>Loại cảnh báo:</th>
                          <td><span className="badge bg-secondary">{selectedAlert.alertType}</span></td>
                        </tr>
                        <tr>
                          <th>Trạng thái:</th>
                          <td>
                            <span className={`badge bg-${getStatusBadgeClass(selectedAlert.status)}`}>
                              {getStatusLabel(selectedAlert.status)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Thời gian:</th>
                          <td><small className="text-muted">{selectedAlert.createdDate}</small></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary">Đánh giá rủi ro</h6>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Điểm cảnh báo:</span>
                        <strong className={`text-${getRiskBadgeClass(selectedAlert.riskLevel)}`}>
                          {selectedAlert.score}/{selectedAlert.maxScore}
                        </strong>
                      </div>
                      <div className="progress" style={{height: '25px'}}>
                        <div 
                          className={`progress-bar bg-${getRiskBadgeClass(selectedAlert.riskLevel)}`} 
                          style={{width: `${selectedAlert.score}%`}}
                        >
                          {selectedAlert.score}%
                        </div>
                      </div>
                    </div>
                    <div className={`alert alert-${getRiskBadgeClass(selectedAlert.riskLevel)}`}>
                      <strong>Mức độ: {getRiskLabel(selectedAlert.riskLevel)}</strong>
                    </div>
                  </div>
                </div>
                
                <div className="alert alert-warning">
                  <i className="bi bi-lightbulb"></i> <strong>Khuyến nghị:</strong>
                  <ul className="mb-0 mt-2">
                    {selectedAlert.recommendations && selectedAlert.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDetailModal(false)}
                >
                  Đóng
                </button>
                {selectedAlert.status === 'new' && (
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={() => {
                      updateAlertStatus(selectedAlert.id, 'Đang xử lý');
                      setShowDetailModal(false);
                    }}
                  >
                    <i className="bi bi-play-fill"></i> Bắt đầu xử lý
                  </button>
                )}
                {selectedAlert.status === 'in_progress' && (
                  <button 
                    type="button" 
                    className="btn btn-info"
                    onClick={() => {
                      updateAlertStatus(selectedAlert.id, 'Đã xử lý');
                      setShowDetailModal(false);
                    }}
                  >
                    <i className="bi bi-check-circle"></i> Hoàn thành
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => processAlert(selectedAlert)}
                >
                  <i className="bi bi-arrow-right-circle"></i> Gửi đến Workflow
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Alerts;
